import { useState, useEffect, useCallback, useMemo } from 'react';

import useAuthStore from '@entities/auth/model/useAuthStore';
import MainLayout from '@shared/components/organisms/MainLayout/MainLayout';
import TabContentLayout from '@shared/components/organisms/TabContentLayout/TabContentLayout';
import ContentSectionLayout from '@shared/components/organisms/ContentSectionLayout/ContentSectionLayout';
import useSort from '@shared/hooks/useSort';
import { useGetVacationProposeListQuery, useVacationTypesQuery } from '@features/vacation/hooks';
import VacationApprovalDetailModal from '@pages/vacation/components/VacationApprovalDetailModal';
import useGetTeamQuery from '@features/team/hooks';

import { useIsMobile } from '@shared/hooks';
import { MobileLayout } from '@shared/components/organisms/mobile';
import MobileVacationApprovalContent from '@pages/vacation/components/mobile/MobileVacationApprovalContent';
import MobileVacationApprovalDetail from '@pages/vacation/components/mobile/MobileVacationApprovalContent/MobileVacationApprovalDetail';

import styles from './VacationApprovalPage.module.scss';

/**
 * VacationApprovalPage 컴포넌트
 * - 휴가결재 페이지 (결재자가 결재해야 하는 휴가 목록)
 */
function VacationApprovalPage() {
  // 모바일 화면 감지
  const isMobile = useIsMobile();
  // 현재 회사 - Zustand store에서 가져오기
  const companyCode = useAuthStore((state) => state.companyCode);

  //선택된 로우 상태값
  const [selectedVacation, setSelectedVacation] = useState(null);

  //부서조회
  const { data = [] } = useGetTeamQuery(companyCode);
  const teamList = data?.resultData?.teamList ?? [];

  // 기본 조회기간 설정: 현재 시간으로부터 3개월 전 ~ 당일
  const defaultDateRange = useMemo(function () {
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 3);

    function formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    return {
      start: formatDate(threeMonthsAgo),
      end: formatDate(today),
    };
  }, []);

  // 필터 값 상태
  const [filterValues, setFilterValues] = useState({
    queryPeriod_start: defaultDateRange.start,
    queryPeriod_end: defaultDateRange.end,
    approvalStatus: '', // 전체
    vacationType: '', // 전체
    teamCode: '', // 전체
    name: '', // 전체
  });

  // 실제 API 조회에 사용되는 검색 조건 (조회 버튼 클릭 시 업데이트)
  const [searchParams, setSearchParams] = useState({
    startDate: defaultDateRange.start.replace(/-/g, ''),
    endDate: defaultDateRange.end.replace(/-/g, ''),
    stateCode: null,
    vacationType: null,
    teamCode: null,
    name: null,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15; // 페이지당 항목 수

  // 휴가 타입 목록 조회
  const { data: vacationTypes = [] } = useVacationTypesQuery();

  // 휴가 결재 목록 조회
  const {
    data: vacationListData,
    isLoading: isLoadingList,
    refetch: refetchVacationList,
  } = useGetVacationProposeListQuery(
    searchParams.startDate,
    searchParams.endDate,
    searchParams.stateCode || undefined,
    searchParams.vacationType || undefined,
    searchParams.teamCode || undefined,
    searchParams.name || undefined,
  );

  // 필터 필드 정의
  const filterFields = useMemo(
    function () {
      // 결재상태 옵션
      const statusOptions = [
        { value: '', label: '전체' },
        { value: '2', label: '결재중' },
        { value: '3', label: '결재완료' },
        { value: '4', label: '반려' },
      ];

      // 휴가구분 옵션
      const vacationTypeOptions = [
        { value: '', label: '전체' },
        ...vacationTypes.map(function (type) {
          return {
            value: type.code?.toString() || '',
            label: type.name || '',
          };
        }),
      ];
      const teamListOptions = [
        { value: '', label: '전체' },
        ...teamList.map(function (type) {
          return {
            value: type.teamCode?.toString() || '',
            label: type.team || '',
          };
        }),
      ];

      return [
        {
          key: 'queryPeriod',
          type: 'dateRange',
          label: '조회기간',
          startKey: 'queryPeriod_start',
          endKey: 'queryPeriod_end',
          startValue: filterValues.queryPeriod_start || '',
          endValue: filterValues.queryPeriod_end || '',
          startPlaceholder: '2025-01-01',
          endPlaceholder: '2025-12-31',
          newRow: false, // 첫 번째 줄
        },
        {
          key: 'approvalStatus',
          type: 'select',
          label: '문서상태',
          value: filterValues.approvalStatus || '',
          options: statusOptions,
          newRow: true, // 두 번째 줄 시작
        },
        {
          key: 'vacationType',
          type: 'select',
          label: '휴가구분',
          value: filterValues.vacationType || '',
          options: vacationTypeOptions,
          newRow: false, // 두 번째 줄에 계속
        },
        {
          key: 'teamCode',
          type: 'select',
          label: '기안부서',
          value: filterValues.teamCode || '',
          options: teamListOptions,
          newRow: true, // 세번째 줄 시작
        },
        {
          key: 'name',
          type: 'input',
          label: '기안자',
          value: filterValues.name || '',
          options: vacationTypeOptions,
          newRow: false, // 세번째 줄에 계속
        },
      ];
    },
    [filterValues, vacationTypes, teamList],
  );

  // 필터 값 변경 핸들러
  const handleFilterChange = useCallback(function (key, value) {
    setFilterValues(function (prev) {
      return {
        ...prev,
        [key]: value,
      };
    });
  }, []);

  // 필터 초기화
  const handleReset = useCallback(() => {
    setFilterValues({
      queryPeriod_start: defaultDateRange.start,
      queryPeriod_end: defaultDateRange.end,
      approvalStatus: '',
      vacationType: '',
      teamCode: '',
      name: '',
    });
  }, [defaultDateRange]);

  // 조회 버튼 클릭
  const handleSearch = useCallback(
    function () {
      // 날짜 형식 변환 (YYYY-MM-DD -> YYYYMMDD)
      const startDate = filterValues.queryPeriod_start?.replace(/-/g, '') || '';
      const endDate = filterValues.queryPeriod_end?.replace(/-/g, '') || '';

      // stateCode 변환 (빈 문자열이면 null)
      const stateCode =
        filterValues.approvalStatus === '' ? null : Number(filterValues.approvalStatus);

      // vacationType 변환 (빈 문자열이면 null)
      const vacationType =
        filterValues.vacationType === '' ? null : Number(filterValues.vacationType);

      // teamcode 반환 (빈 문자열이면 null)
      const teamCode = filterValues.teamCode === '' ? null : Number(filterValues.teamCode);

      // 기안자 이름 반환(빈 문자열이면 null)
      const name = filterValues.name?.trim() || null;

      setSearchParams({
        startDate,
        endDate,
        stateCode,
        vacationType,
        teamCode,
        name,
      });
      setCurrentPage(1);
    },
    [filterValues],
  );

  // 페이지 변경 핸들러
  const handlePageChange = useCallback(function (page) {
    setCurrentPage(page);
  }, []);

  // 테이블 데이터 (API 응답 그대로 사용, 정렬용 flat 필드 추가)
  const tableData = useMemo(() => {
    return (vacationListData || []).map((item) => {
      const typeName = item.vacation_type?.name || '';
      const ccName = item.cc_type?.name;
      const vacationTypeDisplay = ccName ? `${typeName}-${ccName}` : typeName;

      return {
        ...item,
        writerName: item.writerInfo?.name ?? '',
        signerName: item.signerInfo?.name ?? '',
        vacationTypeDisplay,
      };
    });
  }, [vacationListData]);

  // 정렬 훅 적용 (기본값: 신청일자(date) 내림차순)
  const { sortedData, sortDirection, sortField, handleSort } = useSort(tableData, {
    field: 'date',
    direction: 'desc',
  });

  // 날짜 포맷팅 함수 (YYYYMMDD -> YYYY.MM.DD)
  function formatDate(dateString) {
    if (!dateString) return '-';
    if (dateString.includes('.')) return dateString;
    if (dateString.length === 8) {
      return `${dateString.substring(0, 4)}.${dateString.substring(4, 6)}.${dateString.substring(6, 8)}`;
    }
    return dateString;
  }

  // 상태 텍스트 변환
  function getStateText(state) {
    const stateMap = {
      0: '임시저장',
      1: '결재 대기',
      2: '결재중',
      3: '결재완료',
      4: '반려',
      7: '관리자취소',
    };
    return stateMap[state] || '결재중';
  }

  // 테이블 컬럼 정의 (render 함수로 데이터 접근)
  const tableColumns = useMemo(function () {
    return [
      {
        key: 'date',
        label: '신청일자',
        width: '141px',
        sortable: true,
        render: (value) => formatDate(value),
      },
      {
        key: 'state',
        label: '문서상태',
        width: '128px',
        sortable: true,
        render: (value) => getStateText(value),
      },
      {
        key: 'writerName',
        label: '기안자',
        width: '137px',
        sortable: true,
        render: (value) => value || '-',
      },
      {
        key: 'signerName',
        label: '결재자',
        width: '137px',
        sortable: true,
        render: (value) => value || '-',
      },
      {
        key: 'vacationTypeDisplay',
        label: '휴가구분',
        width: '198px',
        sortable: true,
        render: (value) => value || '-',
      },
      {
        key: 'start_date',
        label: '시작일자',
        width: '160px',
        sortable: true,
        render: (value) => formatDate(value),
      },
      {
        key: 'end_date',
        label: '종료일자',
        width: '160px',
        sortable: true,
        render: (value) => formatDate(value),
      },
      {
        key: 'period',
        label: '휴가기간',
        width: '101px',
        sortable: true,
        render: (value) => value ?? '-',
      },
      {
        key: 'reason',
        label: '신청내역',
        width: '165px',
        sortable: true,
        render: (value) => {
          if (!value) return '-';

          const MAX_LENGTH = 14;
          const isEllipsis = value.length > MAX_LENGTH;
          const displayText = isEllipsis ? `${value.slice(0, MAX_LENGTH)}…` : value;

          return <span title={isEllipsis ? value : undefined}>{displayText}</span>;
        },
      },
    ];
  }, []);

  // 현재 페이지에 표시할 데이터 계산
  const totalItems = sortedData.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageData = sortedData.slice(startIndex, endIndex);

  // 초기 로드 시 기본 조회기간으로 자동 조회
  useEffect(
    function () {
      // 컴포넌트 마운트 시에만 초기 조회
      const startDate = defaultDateRange.start.replace(/-/g, '');
      const endDate = defaultDateRange.end.replace(/-/g, '');

      setSearchParams({
        startDate,
        endDate,
        stateCode: null,
        vacationType: null,
      });
      setCurrentPage(1);
    },
    [defaultDateRange],
  );

  // 상세보기 모달 상태값
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // 행 클릭 핸들러
  function handleRowClick(item) {
    setSelectedVacation({ idx: item.idx, enumber: item.writerInfo.enumber });
    setIsDetailModalOpen(true);
  }

  // 상세 닫기 함수
  function handleCloseDetailModal() {
    setIsDetailModalOpen(false);
    setSelectedVacation(null);
  }

  const handleApprovedAndRefresh = useCallback(
    function () {
      // 모달 닫고 목록 갱신
      handleCloseDetailModal();
      refetchVacationList();
    },
    [refetchVacationList],
  );
  const defaultStartDateObj = useMemo(
    () => new Date(defaultDateRange.start),
    [defaultDateRange.start],
  );

  const defaultEndDateObj = useMemo(() => new Date(defaultDateRange.end), [defaultDateRange.end]);

  if (isMobile) {
    return (
      <>
        {isDetailModalOpen ? (
          <MobileVacationApprovalDetail
            onClose={handleCloseDetailModal}
            vacation={selectedVacation}
            onApproved={handleApprovedAndRefresh}
          />
        ) : (
          <MobileLayout title="휴가결재">
            <MobileVacationApprovalContent
              isLoading={isLoadingList}
              vacationData={tableData || []}
              searchParams={searchParams}
              defaultStartDate={defaultStartDateObj}
              defaultEndDate={defaultEndDateObj}
              vacationTypes={vacationTypes}
              teamList={teamList}
              onSearchParams={(params) => {
                setSearchParams(params);
              }}
              onItemClick={handleRowClick}
            />
          </MobileLayout>
        )}
      </>
    );
  }

  return (
    <MainLayout>
      <TabContentLayout
        tabs={{
          label: '휴가 결재',
          content: (
            <ContentSectionLayout
              isLoading={isLoadingList}
              filterFields={filterFields}
              tableColumns={tableColumns}
              paginatedData={currentPageData}
              isEmpty={totalItems === 0}
              emptyMessage="현재 휴가 결재 내역이 없습니다."
              currentPage={currentPage}
              totalPages={totalPages}
              onFilterChange={handleFilterChange}
              onSearch={handleSearch}
              onReset={handleReset}
              onPageChange={handlePageChange}
              classes={styles}
              showExcelButton={false}
              searchButtonText="조회"
              onSort={handleSort}
              sortField={{ field: sortField, direction: sortDirection }}
              onRowClick={handleRowClick}
            />
          ),
        }}
      />
      {/* 휴가 결재 상세 모달 */}
      {isDetailModalOpen && selectedVacation?.idx && (
        <VacationApprovalDetailModal
          isOpen={isDetailModalOpen}
          onClose={handleCloseDetailModal}
          vacation={selectedVacation}
          onApproved={handleApprovedAndRefresh}
          allList={tableData} // 현재 조회된 전체 목록을 전달
        />
      )}
    </MainLayout>
  );
}

export default VacationApprovalPage;
